import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiSearch, HiOutlineAdjustments, HiX } from "react-icons/hi";
import Reveal from "../components/ui/Reveal";
import PlanModal from "../components/plans/PlanModal";
import plansData from "../data/plansData";
import ScrollToTopBTN from "../components/ui/ScrollToTopBTN"
import ScrollToTop from "../components/ui/ScrollToTop";
import { Link } from "react-router-dom";
import CartButton from "../components/CartButton";
import SEO from "../components/SEO";


const categories = ["Todos", "Casa", "Departamento", "Oficina", "Local Comercial", "Casa de campo", "Técnico"];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15, // Tiempo entre la aparición de cada tarjeta
      delayChildren: 0.2,    // Espera antes de iniciar la primera animación
    },
  },
  exit: {
    opacity: 0,
    transition: { staggerChildren: 0.05, staggerDirection: -1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 70,   // Menor rigidez = más suave/lento
      damping: 15      // Evita que rebote demasiado
    }
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } }
};

const PACKAGE_VARIANTS = {
  Basico: {
    label: "Paquete Básico",
    color: "bg-slate-600/60",
    icon: "📄"
  },
  Intermedio: {
    label: "Paquete Pro",
    color: "bg-blue-600/60",
    icon: "📐"
  },
  Completo: {
    label: "Paquete Completo",
    color: "bg-[#F26B1D]/60",
    icon: "📦"
  }
};

const PLAN_CARD_STYLES = {
  Estandar: "bg-white border border-gray-100 shadow-sm hover:shadow-xl",
  Pro: "bg-white border-2 border-blue-500/20 shadow-md shadow-blue-500/5 hover:border-blue-500/40",
  Premium: "bg-white border-2 border-[#F26B1D]/30 shadow-lg shadow-[#F26B1D]/10 hover:shadow-[#F26B1D]/20 hover:border-[#F26B1D]/60"
};

const Plans = () => {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFront, setActiveFront] = useState("Todas"); // Nuevo estado para medidas
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isScrolled, setIsScrolled] = useState(false);

  // Opciones de medidas de frente comunes
  const frontMeasures = ["Todas", "5", "6", "7", "8", "10", "12+"];

  // LÓGICA DE FILTRADO TRIPLE (Categoría + Texto + Medida de Frente)
  const filteredPlans = useMemo(() => {
    return plansData.filter((plan) => {
      const matchesCategory = activeCategory === "Todos" || plan.category === activeCategory;
      const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.area.toLowerCase().includes(searchTerm.toLowerCase());

      const frontValue = parseFloat(plan.front);
      let matchesFront = true;
      if (activeFront !== "Todas") {
        matchesFront = activeFront === "12+" ? frontValue >= 12 : frontValue === parseFloat(activeFront);
      }

      return matchesCategory && matchesSearch && matchesFront;
    });
  }, [searchTerm, activeCategory, activeFront]); // Solo se recalcula si cambian estos valores

  useEffect(() => {
    const handleScroll = () => {
      // Si el usuario baja más de 150px, escondemos el filtro
      setIsScrolled(window.scrollY > 150);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Resetear scroll al cambiar cualquier filtro
  useEffect(() => {
    window.scrollTo({
      top: 300,
      behavior: "smooth" // "smooth" para un deslizamiento elegante o "auto" para instantáneo
    });
  }, [activeCategory, activeFront, searchTerm]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setShowFilters(true);
    }
  }, [isDesktop]);


  return (
    <>
      <SEO
        title={
          activeCategory !== "Todos"
            ? `Planos de ${activeCategory} Modernos`
            : "Catálogo de Planos Arquitectónicos Modernos"
        }
        description="Explora planos arquitectónicos modernos en PDF y AutoCAD DWG. Diseños de casas, departamentos, oficinas y proyectos técnicos disponibles para descarga digital."
        canonical="https://www.planixapp.com/planos"
        image="https://www.planixapp.com/cover.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Catálogo de Planos Arquitectónicos",
          "url": "https://www.planixapp.com/planos",
          "description": "Colección de planos arquitectónicos modernos y diseños digitales.",
          "publisher": {
            "@type": "Organization",
            "name": "Planix"
          }
        }}
      />

      <div className="pt-24 min-h-screen bg-white">
        <ScrollToTop />

        {/* Header */}
        <section className="text-center py-12 px-6">
          <Reveal>
            <h1 className="text-4xl md:text-6xl font-black text-[#1C1C1C] tracking-tight">
              Explora nuestro <span className="text-[#F26B1D]">Catálogo</span>
            </h1>
            <p className="mt-4 text-gray-500 max-w-xl mx-auto text-lg">
              Encuentra el diseño perfecto filtrando por dimensiones, tipo de proyecto o palabras clave.
            </p>
          </Reveal>
        </section>

        {/*<div className="max-w-4xl mx-auto px-6 mb-12">
          <p className="text-gray-600 leading-relaxed text-center">
            Descubre planos arquitectónicos modernos para casas,
            departamentos, oficinas y proyectos comerciales.
            Descarga diseños en formato PDF y AutoCAD DWG,
            ideales para inspiración, construcción y adaptación
            profesional en distintos países.
          </p>
        </div>*/}

        <AnimatePresence>
          {isScrolled && isDesktop && (
            <motion.button
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="fixed right-8 top-24 z-50 flex items-center gap-3 bg-[#F26B1D] text-white pl-4 pr-6 py-3 rounded-full shadow-2xl shadow-[#F26B1D]/40 hover:scale-105 transition-transform font-black text-[10px] uppercase tracking-widest"
            >
              <HiOutlineAdjustments size={20} />
              <span>Refinar Búsqueda</span>
            </motion.button>
          )}
        </AnimatePresence>

        {/* BARRA DE FILTROS MODERNA */}
        <section
          className={`sticky top-16 z-40 bg-white/90 backdrop-blur-md transition-all duration-500 border-b border-gray-100
    ${isScrolled && isDesktop
              ? "-translate-y-full opacity-0 pointer-events-none"
              : "translate-y-0 opacity-100 py-4 px-4 md:px-10"
            }`}
        >
          {/* Header Mobile: Más limpio */}
          <div className="flex items-center justify-between lg:hidden">
            <div className="flex flex-col">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#F26B1D]">Catálogo</span>
              <span className="text-xl font-black text-[#1C1C1C]">Explorar Planos</span>
            </div>

            {/* Este es el botón que faltaba */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-300 ${showFilters
                ? 'bg-[#1C1C1C] text-white rotate-90'
                : 'bg-gray-50 text-gray-600 border border-gray-200 shadow-sm'
                }`}
            >
              {showFilters ? <HiX size={20} /> : <HiOutlineAdjustments size={20} />}
            </button>
          </div>

          <AnimatePresence>
            {isScrolled && isDesktop && (
              <motion.button
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 100, opacity: 0 }}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="fixed right-8 top-24 z-50 flex items-center gap-3 bg-[#F26B1D] text-white pl-4 pr-6 py-3 rounded-full shadow-2xl shadow-[#F26B1D]/40 hover:scale-105 transition-transform font-black text-[10px] uppercase tracking-widest"
              >
                <HiOutlineAdjustments size={20} />
                <span>Refinar Búsqueda</span>
              </motion.button>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {(showFilters || isDesktop) && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden space-y-6 lg:mt-2"
              >
                {/* BUSCADOR Y CATEGORÍAS */}
                <div className="flex flex-col lg:flex-row gap-6 justify-between items-center py-4">
                  <div className="relative w-full lg:max-w-md group">
                    <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#F26B1D] transition-colors size-5" />
                    <input
                      type="text"
                      placeholder="¿Qué proyecto tienes en mente?"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 bg-gray-50/50 rounded-2xl outline-none border-2 border-transparent focus:border-[#F26B1D]/20 focus:bg-white transition-all text-sm font-medium shadow-inner"
                    />
                  </div>

                  <div className="flex gap-2 overflow-x-auto no-scrollbar w-full lg:w-auto pb-2 lg:pb-0">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={`px-6 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${activeCategory === cat
                          ? 'bg-[#F26B1D] text-white shadow-lg shadow-[#F26B1D]/30'
                          : 'bg-white text-gray-400 border border-gray-100 hover:border-gray-300'
                          }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* FILTRO ANCHO DE LOTE */}
                <div className="flex flex-col gap-4 py-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">
                      Filtro por frente (m)
                    </span>
                    {activeFront !== "Todos" && (
                      <button onClick={() => setActiveFront("Todos")} className="text-[10px] font-bold text-[#F26B1D] underline">
                        Limpiar
                      </button>
                    )}
                  </div>

                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                    {frontMeasures.map((measure) => (
                      <button
                        key={measure}
                        onClick={() => setActiveFront(measure)}
                        className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all min-w-[70px] ${activeFront === measure
                          ? "bg-[#1C1C1C] text-white"
                          : "bg-white border border-gray-100 text-gray-400"
                          }`}
                      >
                        {measure} {measure !== "Todos" && "m"}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <p className="text-gray-500 text-sm sm:text-base mt-2 max-w-xl mx-auto">
            Encuentra planos gratis <Link to="/planos-gratis" className="text-[#F26B1D] font-bold underline">aquí</Link> y explora nuestro catálogo.
          </p>
        </section>

        {/* Contador de resultados (Añadir debajo de la sección de filtros) */}
        <div className="max-w-7xl mx-auto px-6 mb-8">
          <p className="text-sm text-gray-500 font-medium">
            Se encontraron <span className="text-[#F26B1D] font-bold">{filteredPlans.length}</span> planos disponibles
          </p>
        </div>

        {/* Grid de Resultados */}
        <section className="py-12 px-6 max-w-7xl mx-auto min-h-[600px]">
          <AnimatePresence mode="wait">
            {filteredPlans.length > 0 ? (
              <motion.div
                // La key dinámica fuerza a que el grid se re-anime al filtrar
                key={activeCategory + activeFront + searchTerm}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {filteredPlans.map((plan) => {
                  const specificStyle = PLAN_CARD_STYLES[plan.planType] || PLAN_CARD_STYLES.Estandar;
                  return (
                    <motion.div
                      layout
                      key={plan.id}
                      variants={cardVariants}
                      whileHover={{ y: -10 }}
                      className={`group rounded-[2rem] transition-all duration-500 overflow-hidden ${specificStyle}`}
                    >
                      {/* IMAGEN */}

                      <Link to={`/planos/${plan.id}`} className="relative block h-64 overflow-hidden">
                        <div
                          className="relative h-64 overflow-hidden cursor-pointer"
                        >
                          <img
                            src={plan.images[0]}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            alt={`${plan.name} - Plano arquitectónico moderno en PDF y AutoCAD`}
                          />

                          {/* ETIQUETA DINÁMICA DE PAQUETE */}
                          {PACKAGE_VARIANTS[plan.packageType] && (
                            <span className={`absolute bottom-4 left-4 ${PACKAGE_VARIANTS[plan.packageType].color} backdrop-blur-md text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg border border-white/20 flex items-center gap-1`}>
                              {PACKAGE_VARIANTS[plan.packageType].icon} {PACKAGE_VARIANTS[plan.packageType].label}
                            </span>
                          )}

                          {/* BADGE */}
                          {plan.badge && (
                            <span className="absolute top-4 left-4 bg-[#F26B1D] text-white text-[16px] font-black px-3 py-1 rounded-full shadow-lg">
                              {plan.badge}
                            </span>
                          )}

                          {/* PLAN TYPE */}
                          <span className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[13px] font-bold text-gray-700">
                            {plan.planType === "Diseño" ? "✏️ Diseño" : "📦 Planos"}
                          </span>

                          {/* HOVER */}
                          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                            <span className="bg-white text-black px-5 py-2 rounded-full text-xs font-bold">
                              Ver Detalles
                            </span>
                          </div>
                        </div>
                      </Link>

                      {/* INFO */}
                      <div className="p-6">
                        {/* TITLE */}
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="text-xl md:text-xs font-bold text-[#1C1C1C] group-hover:text-[#F26B1D] transition-colors leading-tight">
                            {plan.name}
                          </h3>
                        </div>

                        {/* FEATURES */}
                        <div className="flex flex-wrap gap-4 mb-5 text-sm md:text-base font-semibold text-gray-600">

                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-[#F26B1D] rounded-full"></span>
                            {plan.area} m²
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-[#F26B1D] rounded-full"></span>
                            {plan.rooms} Habitaciones
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-[#F26B1D] rounded-full"></span>
                            {plan.dimencion} m²
                          </div>

                          {plan.floors && (
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-[#F26B1D] rounded-full"></span>
                              {plan.floors} piso{plan.floors > 1 && "s"}
                            </div>
                          )}

                        </div>

                        <div className="flex flex-wrap gap-2" >
                          {plan.masVendidos && (
                            <span className="inline-flex items-center gap-1 bg-[#FF0000] text-white px-2 py-0.5 rounded-full text-[12px] font-bold">
                              🔥 Más Vendido
                            </span>
                          )}
                        </div>

                        {/* PRICE SECTION */}
                        <div className="flex items-center justify-between border-t border-gray-100 pt-6">
                          <div className="flex flex-col gap-1">
                            {/* Contenedor de Precio en Soles */}
                            <div className="flex items-baseline gap-2">
                              {/* Precio Actual */}
                              <span className="text-3xl font-black text-[#1C1C1C] tracking-tighter">
                                S/ {plan.price}
                              </span>

                              {/* Solo se muestra si originalPrice es mayor a 0 y mayor al precio de venta */}
                              {Number(plan.originalPrice) > plan.price && (
                                <span className="text-xs text-gray-400 line-through font-medium">
                                  S/ {plan.originalPrice}
                                </span>
                              )}
                            </div>

                            {/* Badge de Dólares Resaltado */}
                            <div className="flex">
                              <div className="inline-flex items-center bg-[#1C1C1C] text-white px-3 py-1 rounded-full shadow-lg shadow-gray-200 group-hover:bg-[#F26B1D] transition-colors duration-500">
                                <span className="text-[10px] font-black uppercase tracking-[0.15em] mr-2 text-white/50">
                                  USD
                                </span>
                                <span className="text-2xs font-black">
                                  {Math.round(plan.price / 3.8)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* BOTÓN DE ACCIÓN TIPO "CARD LINK" */}
                          <Link
                            to={`/planos/${plan.id}`}
                            className="group/btn transition-transform active:scale-90"
                          >
                            <div className="flex items-center gap-2 bg-gray-50 text-[#1C1C1C] pl-4 pr-3 py-3 rounded-2xl group-hover/btn:bg-[#F26B1D] group-hover/btn:text-white transition-all duration-300">
                              <span className="text-[10px] font-black uppercase tracking-widest hidden md:block">
                                Detalles
                              </span>
                              <HiOutlineAdjustments className="text-xl" />
                            </div>
                          </Link>
                        </div>
                      </div>

                    </motion.div>
                  );

                })}

              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-20 text-center"
              >
                <h3 className="text-2xl font-bold text-gray-300">
                  No encontramos planos con esos filtros
                </h3>
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setActiveCategory("Todos");
                    setActiveFront("Todas");
                  }}
                  className="mt-4 text-[#F26B1D] font-bold underline hover:text-[#d45a18] transition-colors"
                >
                  Mostrar todo el catálogo
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* Modal */}
        {selectedPlan && (
          <PlanModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} />
        )}

        <ScrollToTopBTN />

      </div>

      <CartButton />
    </>
  );
};

export default Plans;